import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Alert
} from '@mui/material';
import { generateDefaultCustomerEmail, generateStandardAdvisorEmail, EmailTemplateData } from '../../../services/emailTemplates';

interface EmailPreviewModalProps {
  open: boolean;
  onClose: () => void;
  advisorConfig: {
    name?: string;
    company?: string;
    email?: string;
    automationWelcomeMessage?: string;
    customerId: string;
  } | null;
}

const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({
  open,
  onClose,
  advisorConfig
}) => {
  const [activeTab, setActiveTab] = useState(0);

  // Sample wizard data for preview
  const sampleWizardData = useMemo(() => ({
    personal: {
      client1: {
        firstName: 'John',
        lastName: 'Doe'
      },
      client2: {
        firstName: 'Jane',
        lastName: 'Doe'
      },
      hasPartner: true,
      contact: {
        email: 'john.doe@example.com',
        phone: '(555) 123-4567'
      }
    },
    employment: {
      client1Status: 'employed' as const,
      client2Status: 'employed' as const
    },
    socialSecurity: {
      client1Age: 67,
      client2Age: 65
    },
    savings: {
      taxDeferred: [],
      taxFree: [],
      taxable: []
    },
    goals: {
      targetIncome: 75000,
      priorities: []
    }
  }), []);

  // Build email template data
  const emailData: EmailTemplateData = useMemo(() => ({
    wizardData: sampleWizardData as any,
    advisor: advisorConfig ? {
      customerId: advisorConfig.customerId,
      name: advisorConfig.name || 'Advisor Name',
      company: advisorConfig.company || 'Financial Services',
      email: advisorConfig.email || 'advisor@example.com',
      phone: '',
      advisorPhotoUrl: '',
      companyLogoUrl: '',
      automationWelcomeMessage: advisorConfig.automationWelcomeMessage || '',
      features: {
        emailConfig: {
          intake: { sendToClient: true, sendToAdvisor: true, sendToSupport: false },
          presentationReady: { sendToClient: false, sendToAdvisor: false }
        },
        apiConfig: {
          useCustomUrl: false,
          customUrl: '',
          additionalPayloadFields: {},
        },
        branding: {
          showAdvisorInfo: true,
          automationWelcomeMessage: advisorConfig.automationWelcomeMessage
        }
      }
    } : null,
    customerName: 'John Doe',
    advisorLink: `https://clientinterview.planscout.com/wizard/${advisorConfig?.customerId || '000'}`,
    customTemplateData: {}
  }), [sampleWizardData, advisorConfig]);

  // Generate email previews
  const customerEmailHtml = useMemo(() => {
    if (!advisorConfig) return '';

    let email = generateDefaultCustomerEmail(emailData);

    // Replace default message with custom welcome message if provided
    if (advisorConfig.automationWelcomeMessage) {
      email = email.replace(
        '<p>Thank you for completing your financial assessment with PlanScout. Our team is working on your personalized financial plan and will have it ready for you in five business days.</p>',
        `<p>${advisorConfig.automationWelcomeMessage}</p>`
      );
    }

    return email;
  }, [emailData, advisorConfig]);

  const advisorEmailHtml = useMemo(() => {
    if (!advisorConfig) return '';
    return generateStandardAdvisorEmail(emailData);
  }, [emailData, advisorConfig]);

  if (!advisorConfig) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Email Preview
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          This is a preview of the email templates that will be sent when a client submits through your form. The preview uses sample data.
        </Alert>

        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
          <Tab label="Customer Email" />
          <Tab label="Advisor Notification" />
        </Tabs>

        {activeTab === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This email is sent to the client after they complete the form.
            </Typography>
            <Box
              sx={{
                border: '1px solid #ddd',
                borderRadius: 1,
                p: 2,
                bgcolor: '#f9f9f9',
                maxHeight: '500px',
                overflow: 'auto'
              }}
              dangerouslySetInnerHTML={{ __html: customerEmailHtml }}
            />
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This email is sent to you when a client submits through your form.
            </Typography>
            <Box
              sx={{
                border: '1px solid #ddd',
                borderRadius: 1,
                p: 2,
                bgcolor: '#f9f9f9',
                maxHeight: '500px',
                overflow: 'auto'
              }}
              dangerouslySetInnerHTML={{ __html: advisorEmailHtml }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailPreviewModal;
